import { useState, useRef } from 'react'
import { Message, MessageStatus } from '../services/api'

interface MediaData {
  type: 'image' | 'video' | 'audio' | 'file'
  url: string
  id?: string
  name?: string
  size?: number
  duration?: number
}

interface ChatBubbleProps {
  message: Message
  isSent: boolean
  formatTime: (timestamp: string) => string
  onReply?: (message: Message) => void
  onReaction?: (messageId: string, emoji: string) => void
  onDelete?: (messageId: string) => void
  replyMessage?: Message | null
}

const isSafeUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' || parsed.pathname.startsWith('/api/')
  } catch {
    return url.startsWith('/')
  }
}

const parseMediaMessage = (text: string): MediaData | null => {
  if (!text.startsWith('[MEDIA]')) return null
  try {
    const json = text.slice(7)
    const data = JSON.parse(json) as MediaData
    if (data.url && !isSafeUrl(data.url)) {
      console.warn('[ChatBubble] Blocked unsafe media URL:', data.url)
      return null
    }
    return data
  } catch {
    return null
  }
}

const QUICK_EMOJIS = ['❤️', '👍', '😂', '😮', '😢', '🙏']

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

export default function ChatBubble({ message, isSent, formatTime, onReply, onReaction, onDelete, replyMessage }: ChatBubbleProps) {
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [mediaLoaded, setMediaLoaded] = useState(false)
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const longPressTimer = useRef<any>(null)
  const [audioPlaying, setAudioPlaying] = useState(false)
  const [audioProgress, setAudioProgress] = useState(0)
  const [audioDuration, setAudioDuration] = useState(0)
  const audioRef = useRef<HTMLAudioElement>(null)

  const mediaData = parseMediaMessage(message.text)

  // Long press handler for context menu
  const handleTouchStart = () => {
    longPressTimer.current = setTimeout(() => {
      setShowContextMenu(true)
    }, 500)
  }

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
    }
  }

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowContextMenu(true)
  }

  // Audio player controls
  const toggleAudio = () => {
    if (!audioRef.current) return
    if (audioPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setAudioPlaying(!audioPlaying)
  }

  const handleAudioTimeUpdate = () => {
    if (!audioRef.current) return
    const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100
    setAudioProgress(progress)
  }

  const handleAudioLoaded = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration)
    }
  }

  const handleAudioEnded = () => {
    setAudioPlaying(false)
    setAudioProgress(0)
  }

  const formatDuration = (sec: number): string => {
    if (!sec || !isFinite(sec)) return '0:00'
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60)
    return `${m}:${String(s).padStart(2, '0')}`
  }

  const getStatusIcon = (status: MessageStatus) => {
    switch (status) {
      case 'sending':
        return (
          <div className="w-3 h-3 border border-white/50 border-t-white rounded-full animate-spin" />
        )
      case 'sent':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-white/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'delivered':
        return (
          <span className="flex text-white/70">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 -ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </span>
        )
      case 'read':
        return (
          <span className="flex text-blue-300">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 -ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </span>
        )
      case 'failed':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      default:
        return null
    }
  }

  if (message.deleted) {
    return (
      <div className={`flex ${isSent ? 'justify-end' : 'justify-start'} animate-fade-in`}>
        <div className={`px-4 py-2 rounded-2xl italic text-sm ${isSent ? 'text-white/50 bg-sent-bubble/50 ml-16' : 'text-gray-400 bg-gray-100 dark:bg-gray-800 mr-16'}`}>
          🚫 此訊息已刪除
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        className={`flex ${isSent ? 'justify-end' : 'justify-start'} animate-fade-in`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onContextMenu={handleContextMenu}
      >
        <div
          className={`
            ${isSent ? 'chat-bubble-sent ml-16' : 'chat-bubble-received mr-16'}
            ${message.status === 'failed' ? 'border-l-2 border-error' : ''}
            relative group
          `}
        >
          {/* Reply reference */}
          {replyMessage && (
            <div className={`mb-2 px-3 py-1.5 rounded-lg border-l-2 text-xs ${isSent ? 'bg-white/10 border-white/50' : 'bg-gray-200 dark:bg-gray-700 border-primary'}`}>
              <p className={`font-medium ${isSent ? 'text-white/80' : 'text-primary'}`}>
                {replyMessage.senderId === message.senderId ? '你' : '對方'}
              </p>
              <p className={`truncate ${isSent ? 'text-white/60' : 'text-gray-500 dark:text-gray-400'}`}>
                {replyMessage.text?.startsWith('[MEDIA]') ? '📎 媒體訊息' : replyMessage.text}
              </p>
            </div>
          )}

          {/* Message Content */}
          {mediaData ? (
            <div className="relative">
              {!mediaLoaded && mediaData.type !== 'file' && mediaData.type !== 'audio' && (
                <div className="w-48 h-32 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <div className="spinner border-primary border-t-primary-light" />
                </div>
              )}

              {mediaData.type === 'image' ? (
                <img
                  src={mediaData.url}
                  alt={mediaData.name || 'Image'}
                  className={`max-w-xs rounded-lg cursor-pointer ${mediaLoaded ? '' : 'hidden'}`}
                  onLoad={() => setMediaLoaded(true)}
                  onClick={() => setIsFullscreen(true)}
                />
              ) : mediaData.type === 'video' ? (
                <video
                  src={mediaData.url}
                  className={`max-w-xs rounded-lg ${mediaLoaded ? '' : 'hidden'}`}
                  onLoadedData={() => setMediaLoaded(true)}
                  controls
                />
              ) : mediaData.type === 'audio' ? (
                <div className="w-64 flex items-center gap-2">
                  <audio
                    ref={audioRef}
                    src={mediaData.url}
                    onTimeUpdate={handleAudioTimeUpdate}
                    onLoadedMetadata={handleAudioLoaded}
                    onEnded={handleAudioEnded}
                    preload="metadata"
                  />
                  <button
                    onClick={toggleAudio}
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isSent ? 'bg-white/20 hover:bg-white/30' : 'bg-primary hover:bg-primary-dark'}`}
                  >
                    {audioPlaying ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isSent ? 'text-white' : 'text-white'}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isSent ? 'text-white' : 'text-white'}`} fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`h-1 rounded-full overflow-hidden ${isSent ? 'bg-white/20' : 'bg-gray-300 dark:bg-gray-600'}`}>
                      <div
                        className={`h-full rounded-full transition-all ${isSent ? 'bg-white' : 'bg-primary'}`}
                        style={{ width: `${audioProgress}%` }}
                      />
                    </div>
                    <span className={`text-xs mt-0.5 block ${isSent ? 'text-white/60' : 'text-gray-500 dark:text-gray-400'}`}>
                      {formatDuration(audioDuration)}
                    </span>
                  </div>
                </div>
              ) : (
                <a
                  href={mediaData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-3 p-3 rounded-lg hover:opacity-80 transition-opacity max-w-xs ${isSent ? 'bg-white/10' : 'bg-gray-100 dark:bg-gray-800'}`}
                >
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 011.414.586l5.414 5.414a1 1 0 01.586 1.414V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${isSent ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
                      {mediaData.name || '文件'}
                    </p>
                    {mediaData.size && (
                      <p className={`text-xs ${isSent ? 'text-white/60' : 'text-gray-500'}`}>
                        {formatFileSize(mediaData.size)}
                      </p>
                    )}
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isSent ? 'text-white/60' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </a>
              )}

              {/* Fullscreen modal */}
              {isFullscreen && (
                <div
                  className="fixed inset-0 z-50 bg-black flex items-center justify-center"
                  onClick={() => setIsFullscreen(false)}
                >
                  <button
                    className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-full z-50"
                    onClick={() => setIsFullscreen(false)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <img
                    src={mediaData.url}
                    alt={mediaData.name || 'Image'}
                    className="max-w-full max-h-full object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
            </div>
          ) : (
            <p className="whitespace-pre-wrap break-words">{message.text}</p>
          )}

          {/* Edited indicator */}
          {message.edited && (
            <span className={`text-xs italic ${isSent ? 'text-white/50' : 'text-gray-400'}`}> (已編輯)</span>
          )}

          {/* Reactions */}
          {message.reactions && Object.keys(message.reactions).length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {Object.entries(message.reactions).map(([emoji, users]) => (
                <button
                  key={emoji}
                  onClick={() => onReaction?.(message.id, emoji)}
                  className={`text-xs px-1.5 py-0.5 rounded-full border ${isSent ? 'border-white/20 bg-white/10' : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'}`}
                >
                  {emoji} {users.length > 1 ? users.length : ''}
                </button>
              ))}
            </div>
          )}

          {/* Time and Status */}
          <div className={`flex items-center gap-1 mt-1 ${isSent ? 'justify-end' : ''}`}>
            <span className={`text-xs ${isSent ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
              {formatTime(message.timestamp)}
            </span>
            {isSent && getStatusIcon(message.status)}
          </div>

          {/* Failed message retry button */}
          {message.status === 'failed' && (
            <button
              className="absolute -right-8 top-1/2 -translate-y-1/2 p-1
                         text-error hover:bg-error/10 rounded-full
                         opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Context Menu (Long Press / Right Click) */}
      {showContextMenu && (
        <div className="fixed inset-0 z-50" onClick={() => { setShowContextMenu(false); setShowEmojiPicker(false) }}>
          <div
            className={`absolute z-50 bg-white dark:bg-dark-surface rounded-xl shadow-xl overflow-hidden animate-fade-in ${isSent ? 'right-4' : 'left-4'}`}
            style={{ top: '40%' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Quick emoji row */}
            {!showEmojiPicker && (
              <div className="flex gap-1 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                {QUICK_EMOJIS.map(emoji => (
                  <button
                    key={emoji}
                    className="text-xl p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                    onClick={() => { onReaction?.(message.id, emoji); setShowContextMenu(false) }}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
            <button
              onClick={() => { onReply?.(message); setShowContextMenu(false) }}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              回覆
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(mediaData ? (mediaData.url || '') : message.text)
                setShowContextMenu(false)
              }}
              className="w-full px-4 py-2.5 text-left text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
              </svg>
              複製
            </button>
            {isSent && (
              <button
                onClick={() => { onDelete?.(message.id); setShowContextMenu(false) }}
                className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                刪除
              </button>
            )}
          </div>
        </div>
      )}
    </>
  )
}

// Additional chat-related components

interface TypingIndicatorProps {
  username: string
}

export function TypingIndicator({ username }: TypingIndicatorProps) {
  return (
    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm px-4 py-2">
      <div className="flex gap-1">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
      <span>{username} 正在輸入...</span>
    </div>
  )
}

interface DateDividerProps {
  date: string
}

export function DateDivider({ date }: DateDividerProps) {
  return (
    <div className="flex items-center justify-center my-4">
      <span className="bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs px-3 py-1 rounded-full">
        {date}
      </span>
    </div>
  )
}

export function EncryptionBadge() {
  return (
    <div className="flex flex-col items-center justify-center my-8 text-center">
      <div className="w-16 h-16 bg-secure-green/10 rounded-full flex items-center justify-center mb-3">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <p className="text-secure-green text-sm font-medium mb-1">
        Messages are end-to-end encrypted
      </p>
      <p className="text-gray-500 dark:text-gray-400 text-xs max-w-xs">
        No one outside of this chat, not even LYNKSTAR, can read or listen to them.
      </p>
    </div>
  )
}
